/**
 * Framework Notifications inbox — layout aligned with chat + Tasks
 * (stone palette, serif title, collapsible bodies).
 *
 * Contract:
 *   GET  {apiBase}              → { items: NotificationItem[] }
 *   POST {apiBase}/:id/read     → { ok: true }
 *   POST {apiBase}/read-all     → { ok: true }  (optional; falls back to per-id)
 *   GET  {apiBase}/unread-count → { count: number }
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bell,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  MessageSquare,
  RefreshCw,
} from 'lucide-react';
import { MarkdownRenderer } from '../components/MarkdownRenderer.js';
import { firstReportUrl } from '../lib/report-urls.js';

export interface NotificationItem {
  id: string;
  title: string;
  body?: string;
  severity?: 'info' | 'medium' | 'high';
  created_at: string;
  read: boolean;
  href?: string;
  status_hint?: 'success' | 'failure' | null;
  task_id?: string | null;
}

interface NotificationsPageProps {
  apiBase: string;
  title: string;
  navigate: (path: string) => void;
  onChanged?: () => void;
  viewerSlug: string;
}

function severityStyle(s?: string, statusHint?: string | null): {
  accent: string;
  pill: string;
  label: string;
} {
  if (statusHint === 'failure' || s === 'high') {
    return {
      accent: 'bg-rose-500',
      pill: 'bg-rose-50 text-rose-800 ring-rose-200',
      label: statusHint === 'failure' ? 'Failed' : 'High',
    };
  }
  if (statusHint === 'success') {
    return {
      accent: 'bg-emerald-500',
      pill: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
      label: 'Success',
    };
  }
  if (s === 'medium') {
    return {
      accent: 'bg-amber-400',
      pill: 'bg-amber-50 text-amber-900 ring-amber-200',
      label: 'Medium',
    };
  }
  return {
    accent: 'bg-sky-500',
    pill: 'bg-stone-100 text-stone-600 ring-stone-200',
    label: 'Info',
  };
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function plainPreview(raw: string, max = 160): string {
  let s = raw
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\|.*\|$/gm, ' ')
    .replace(/^[-*_]{3,}\s*$/gm, ' ')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max).trim()}…`;
}

function NotifCard({
  item,
  viewerSlug,
  onMarkRead,
}: {
  item: NotificationItem;
  viewerSlug: string;
  onMarkRead: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const body = item.body?.trim() || '';
  const st = severityStyle(item.severity, item.status_hint);
  const isFail = item.status_hint === 'failure' || item.severity === 'high';
  const preview = body ? plainPreview(body) : null;
  // Explicit href wins; otherwise only BinDrive /reports URLs from body text.
  const link = item.href?.trim() || (body ? firstReportUrl(body) : null);
  const canExpand = Boolean(body);

  return (
    <article
      className={`rounded-2xl border bg-white shadow-sm transition hover:shadow ${
        item.read
          ? 'border-stone-200/80 opacity-[0.92]'
          : 'border-stone-300/90 ring-1 ring-stone-900/[0.03]'
      }`}
    >
      <div className="flex gap-3 p-4 sm:p-5">
        <span
          className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${st.accent} ${
            item.read ? 'opacity-50' : ''
          }`}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-serif text-base font-semibold tracking-tight text-stone-900 sm:text-[1.05rem]">
                  {item.title}
                </h2>
                {!item.read && (
                  <span className="rounded-full bg-stone-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                    new
                  </span>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${st.pill}`}
                >
                  {st.label}
                </span>
                <span className="text-[12px] text-stone-400">{formatWhen(item.created_at)}</span>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5">
              {link && (
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-stone-200 bg-stone-50 px-2.5 py-1.5 text-[11px] font-medium text-stone-700 hover:bg-stone-100"
                >
                  Open <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {!item.read && (
                <button
                  type="button"
                  onClick={() => onMarkRead(item.id)}
                  className="inline-flex items-center gap-1 rounded-lg border border-stone-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-stone-700 hover:bg-stone-50"
                >
                  Mark read
                </button>
              )}
            </div>
          </div>

          {canExpand && (
            <div className="mt-3 border-t border-stone-100 pt-3">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-start gap-1.5 text-left"
              >
                {open ? (
                  <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
                ) : (
                  <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-stone-400">
                    Message
                  </div>
                  {!open && preview && (
                    <p
                      className={`mt-0.5 line-clamp-2 text-sm leading-relaxed ${
                        isFail ? 'text-rose-700' : 'text-stone-600'
                      }`}
                    >
                      {preview}
                    </p>
                  )}
                </div>
              </button>
              {open && (
                <div
                  className={`mt-2 max-h-72 overflow-y-auto rounded-xl border px-3 py-2.5 text-sm leading-relaxed ${
                    isFail
                      ? 'border-rose-100 bg-rose-50/60 text-rose-900'
                      : 'border-stone-100 bg-stone-50/80 text-stone-800'
                  }`}
                >
                  {isFail ? (
                    <p className="whitespace-pre-wrap">{body}</p>
                  ) : (
                    <div className="max-w-none text-sm [&_pre]:text-xs [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:text-xs">
                      <MarkdownRenderer text={body} viewerSlug={viewerSlug} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export function NotificationsPage({
  apiBase,
  title,
  navigate,
  onChanged,
  viewerSlug,
}: NotificationsPageProps) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  const load = useCallback(async () => {
    if (!apiBase) {
      setError('apiBase not configured for notifications page');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(apiBase, { credentials: 'include' });
      if (!res.ok) throw new Error(`Failed to load notifications (${res.status})`);
      const data = (await res.json()) as { items?: NotificationItem[] };
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    void load();
  }, [load]);

  const markRead = async (id: string) => {
    try {
      const res = await fetch(`${apiBase}/${encodeURIComponent(id)}/read`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`mark read failed (${res.status})`);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, read: true } : it)));
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const markAllRead = async () => {
    const unread = items.filter((i) => !i.read);
    if (unread.length === 0) return;
    setMarkingAll(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/read-all`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        setItems((prev) => prev.map((it) => ({ ...it, read: true })));
        onChanged?.();
        return;
      }
      // Fallback: mark one-by-one
      for (const it of unread) {
        await markRead(it.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadCount = useMemo(() => items.filter((i) => !i.read).length, [items]);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-stone-50/80">
      <div className="shrink-0 border-b border-stone-200/80 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-stone-500" />
              <h1 className="font-serif text-lg font-semibold tracking-tight text-stone-900">
                {title}
              </h1>
              {unreadCount > 0 && (
                <span className="rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-stone-500">
              {loading
                ? 'Loading…'
                : items.length === 0
                  ? 'Inbox is empty'
                  : `${items.length} notification${items.length === 1 ? '' : 's'}${
                      unreadCount ? ` · ${unreadCount} unread` : ' · all read'
                    }`}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
            >
              <MessageSquare className="h-3.5 w-3.5" /> Chat
            </button>
            <button
              type="button"
              onClick={() => void markAllRead()}
              disabled={markingAll || unreadCount === 0}
              className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              {markingAll ? 'Marking…' : 'Mark all read'}
            </button>
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-stone-800 disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-5 sm:px-6 sm:py-6">
          <p className="mb-5 text-sm leading-relaxed text-stone-600">
            Task runs and system alerts land here. Use{' '}
            <code className="rounded-md bg-stone-200/70 px-1.5 py-0.5 text-[12px] text-stone-800">
              /notifications
            </code>{' '}
            on Telegram or Slack to manage them from chat apps.
          </p>

          {error && (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800">
              {error}
            </div>
          )}

          {loading && items.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-20 text-stone-400">
              <Loader2 className="h-7 w-7 animate-spin" />
              <span className="text-sm">Loading notifications…</span>
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-6 py-14 text-center shadow-sm">
              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-stone-100">
                <Bell className="h-5 w-5 text-stone-500" />
              </div>
              <p className="font-serif text-base font-semibold text-stone-900">All clear</p>
              <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-stone-500">
                When a scheduled task finishes — or the system has something to share — it will
                show up here.
              </p>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-3 py-2 text-xs font-medium text-white hover:bg-stone-800"
              >
                <MessageSquare className="h-3.5 w-3.5" /> Open chat
              </button>
            </div>
          )}

          {items.length > 0 && (
            <ul className="space-y-3">
              {items.map((it) => (
                <li key={it.id}>
                  <NotifCard
                    item={it}
                    viewerSlug={viewerSlug}
                    onMarkRead={(id) => void markRead(id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
